import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Grid,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Divider as MuiDivider
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ViewList as ViewListIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
  SelectAll as SelectAllIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import config from '../../../../config.json';

/**
 * C-1 & C-3: 任務條視圖組件（Trello 看板風格）
 * 將任務進度從矩陣表格改為「任務條/磚塊/火車車廂式」呈現
 * 支持每組獨立的任務流程
 * 按狀態分欄：排程、已派發、進行中、已完成
 */
export default function TaskBlocksView({ 
  data, 
  courseDispatchId,
  onTaskReorder,
  onTaskDelete,
  onTaskAdd,
  onCellClick,
  getStatusColor,
  getStatusText,
  onDataChange // 數據更新回調
}) {
  const [expandedGroups, setExpandedGroups] = useState({}); // 小組展開狀態
  const [expandedStatuses, setExpandedStatuses] = useState({}); // 狀態欄展開狀態（以 groupId_status 為 key）
  // 從 sessionStorage 恢復視圖模式，如果沒有則默認為 'task'（任務視圖優先）
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = sessionStorage.getItem(`taskBlocksViewMode_${courseDispatchId}`);
    return savedViewMode || 'task';
  });
  const [isAssigning, setIsAssigning] = useState(false); // 防止重複提交
  const scrollContainerRef = useRef(null); // 用於滾動位置恢復
  const lastScrollPositionRef = useRef(0); // 保存最後的滾動位置
  const lastOperatedTaskIdRef = useRef(null); // 保存最後操作的任務ID

  // 添加調試日誌，追蹤 data 的變化
  useEffect(() => {
    console.log('🔄 [TaskBlocksView] data 已更新');
    console.log('  - 任務數量:', data?.tasks?.length || 0);
    console.log('  - 小組數量:', data?.groups?.length || 0);
    console.log('  - 任務列表:', data?.tasks?.map(t => ({ id: t.id, title: t.title, assigned_groups: t.assigned_groups })) || []);
    console.log('  - 當前視圖模式:', viewMode);
  }, [data, viewMode]);
  const [openTaskSelectDialog, setOpenTaskSelectDialog] = useState(false);
  const [selectedTaskForAdd, setSelectedTaskForAdd] = useState(null); // { groupId, status }
  const [selectedTasks, setSelectedTasks] = useState([]); // 選中的任務ID列表
  const [openStatusConfirmDialog, setOpenStatusConfirmDialog] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState(null); // { taskId, groupId, currentStatus, newStatus }
  const [groupMembersAnchor, setGroupMembersAnchor] = useState(null); // Popover anchor
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState(null); // 選中的組別用於顯示成員

  // 狀態欄位定義
  const statusColumns = [
    { 
      id: 'not_dispatched', 
      label: '排程', 
      color: '#f5f5f5',
      textColor: '#666'
    },
    { 
      id: 'dispatched', 
      label: '已派發', 
      color: '#e3f2fd',
      textColor: '#1976d2'
    },
    { 
      id: 'in_progress', 
      label: '進行中', 
      color: '#fff3e0',
      textColor: '#f57c00'
    },
    { 
      id: 'completed', 
      label: '已完成', 
      color: '#e8f5e8',
      textColor: '#388e3c'
    }
  ];

  // 為每組構建任務序列，按狀態分類
  const groupTaskSequences = useMemo(() => {
    if (!data || !data.groups || !Array.isArray(data.groups) || !data.tasks || !Array.isArray(data.tasks)) {
      console.warn('⚠️ [TaskBlocksView] 小組視圖數據無效:', data);
      return {};
    }
    
    const sequences = {};
    
    // 按組別ID排序（確保組別順序一致）
    const sortedGroups = [...data.groups].sort((a, b) => {
      // 優先按ID排序（數字或字符串）
      const idA = parseInt(a.id) || 0;
      const idB = parseInt(b.id) || 0;
      if (idA !== idB) return idA - idB;
      // 如果ID相同，按名稱排序
      const nameA = a.name || a.groupName || '';
      const nameB = b.name || b.groupName || '';
      return nameA.localeCompare(nameB, 'zh-TW');
    });
    
    sortedGroups.forEach(group => {
      // 獲取該組相關的任務
      // 調整：不僅依賴 assigned_groups，也看 matrixStatus 中是否有這個 task-group 的狀態
      const groupTasks = data.tasks.filter(task => {
        const assignedGroups = task.assigned_groups || [];
        const assignedGroupIds = assignedGroups.map(id => parseInt(id));
        const groupIdInt = parseInt(group.id);
        
        // 只有當任務在 assigned_groups 中時，才應該顯示任務
        // 或者，如果任務不在 assigned_groups 中，但 customizations 中有該小組的狀態記錄，也應該顯示（表示曾經被指派過）
        if (assignedGroupIds.includes(groupIdInt)) {
          return true;
        }
        
        // 檢查 customizations 中是否有該小組的狀態記錄
        const groupStatusKey = `group_${groupIdInt}_status`;
        const hasCustomStatus = task.customizations && task.customizations[groupStatusKey];
        
        // 如果有 customizations 記錄，說明任務曾經被指派過，應該顯示
        return !!hasCustomStatus;
      });

      // 按狀態分類任務
      const tasksByStatus = {
        not_dispatched: [],
        dispatched: [],
        in_progress: [],
        completed: []
      };

      groupTasks.forEach(task => {
        const key = `${task.id}_${group.id}`;
        const cellData = data.matrixStatus[key] || {};
        const status = cellData.status || 'not_dispatched';
        
        tasksByStatus[status].push({
          ...task,
          // 優先使用直接屬性，若無則從 customizations 提取 unit_number 和 subtask_number
          unit_number: task.unit_number ?? task.customizations?.unit_number ?? null,
          subtask_number: task.subtask_number ?? task.customizations?.subtask_number ?? null,
          status,
          progress: cellData.progress,
          studentResult: cellData.studentResult
        });
      });

      // 每個狀態內的任務按章節/子任務排序（優先使用 unit_number 和 subtask_number）
      Object.keys(tasksByStatus).forEach(status => {
        tasksByStatus[status].sort((a, b) => {
          // 優先使用直接屬性，若無則從 customizations 取得章節/子任務
          const aUnit = a.unit_number ?? a.customizations?.unit_number ?? 1;
          const bUnit = b.unit_number ?? b.customizations?.unit_number ?? 1;
          const aSubtask = a.subtask_number ?? a.customizations?.subtask_number ?? null;
          const bSubtask = b.subtask_number ?? b.customizations?.subtask_number ?? null;
          
          // 優先按章節號排序
          if (aUnit !== bUnit) {
            return aUnit - bUnit;
          }
          
          // 同章節內按子任務號排序
          if (aSubtask && bSubtask) {
            // 嘗試解析為數字
            const aSubNum = parseInt(aSubtask);
            const bSubNum = parseInt(bSubtask);
            if (!isNaN(aSubNum) && !isNaN(bSubNum)) {
              return aSubNum - bSubNum;
            }
            // 如果無法解析為數字，按字串排序
            return aSubtask.localeCompare(bSubtask);
          }
          
          // 如果沒有子任務號，按 order_index 排序
          return (a.order_index || 0) - (b.order_index || 0);
        });
      });

      sequences[group.id] = {
        group,
        tasksByStatus
      };
    });

    return sequences;
  }, [data.tasks, data.groups, data.matrixStatus]);

  // 指派任務給小組
  const handleAssignTaskToGroup = async (taskId, groupId) => {
    try {
      const task = data.tasks.find(t => t.id === taskId);
      if (!task) return;

      const currentAssignedGroups = task.assigned_groups || [];
      if (currentAssignedGroups.includes(groupId)) {
        return; // 已經指派過了
      }

      const newAssignedGroups = [...currentAssignedGroups, groupId];
      
      // 調用 API 指派任務
      await axiosInstance.put(`${config.assignApplicationTask}${taskId}/assign`, {
        assigned_groups: newAssignedGroups
      });

      // 刷新數據
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      console.error('指派任務錯誤:', error);
    }
  };

  // 打開任務選擇對話框
  // 在小組視圖中：groupId 是小組ID，用於為該小組選擇任務
  // 在任務視圖中：groupId 是任務ID，用於為該任務選擇小組
  const handleOpenTaskSelectDialog = (groupId, status, isTaskView = false) => {
    setSelectedTaskForAdd({ groupId, status, isTaskView });
    setSelectedTasks([]);
    setOpenTaskSelectDialog(true);
  };

  // 確認選擇任務並指派
  const handleConfirmTaskSelect = async () => {
    if (selectedTasks.length === 0 || !selectedTaskForAdd) {
      return;
    }

    // 防止重複提交
    if (isAssigning) {
      console.warn('⚠️ [handleConfirmTaskSelect] 任務正在指派中，忽略重複請求');
      return;
    }

    try {
      setIsAssigning(true); // 設置指派中狀態
      console.log('🔄 [handleConfirmTaskSelect] 開始指派任務...');
      
      const { groupId, isTaskView } = selectedTaskForAdd;

      if (isTaskView) {
        // 任務視圖：為指定任務指派給選中的小組
        const taskId = groupId; // 在任務視圖中，groupId 實際上是 taskId
        const task = data.tasks.find(t => t.id === taskId);
        const currentAssignedGroups = task?.assigned_groups || [];
        const newAssignedGroups = [...new Set([...currentAssignedGroups, ...selectedTasks])]; // selectedTasks 是小組ID列表
        
        await axiosInstance.put(`${config.assignApplicationTask}${taskId}/assign`, {
          assigned_groups: newAssignedGroups
        });
      } else {
        // 小組視圖：為選中的任務指派給指定小組（狀態為排程）
        const dispatchPromises = selectedTasks.map(taskId => {
          const task = data.tasks.find(t => t.id === taskId);
          const currentAssignedGroups = task?.assigned_groups || [];
          const newAssignedGroups = [...new Set([...currentAssignedGroups, groupId])];
          
          return axiosInstance.put(`${config.assignApplicationTask}${taskId}/assign`, {
            assigned_groups: newAssignedGroups
          });
        });

        await Promise.all(dispatchPromises);

        // 任務狀態會在數據刷新後自動更新為排程狀態（not_dispatched）
        // 不需要額外調用 onCellClick，因為 assign API 會自動設置初始狀態
      }

      // 保存當前操作的任務ID（用於滾動恢復）
      if (isTaskView) {
        lastOperatedTaskIdRef.current = groupId; // 在任務視圖中，groupId 是 taskId
      }
      
      // 保存當前滾動位置
      if (scrollContainerRef.current) {
        lastScrollPositionRef.current = scrollContainerRef.current.scrollTop;
      }

      // 刷新數據
      if (onDataChange) {
        await onDataChange();
      }

      setOpenTaskSelectDialog(false);
      setSelectedTaskForAdd(null);
      setSelectedTasks([]);
      
      // 確保保持在任務視圖模式
      if (isTaskView && viewMode !== 'task') {
        setViewMode('task');
        sessionStorage.setItem(`taskBlocksViewMode_${courseDispatchId}`, 'task');
      }
    } catch (error) {
      console.error('指派任務錯誤:', error);
    } finally {
      setIsAssigning(false); // 重置指派中狀態
      console.log('✅ [handleConfirmTaskSelect] 指派流程結束');
    }
  };

  // 獲取可選擇的任務（未指派給該組的任務）- 小組視圖使用
  // 只顯示不在 assigned_groups 中的任務（已刪除的任務）
  // 注意：需要檢查 assigned_groups 和 customizations（不檢查 matrixStatus，因為它會為所有組合創建記錄）
  const getAvailableTasks = (groupId) => {
    const groupIdInt = parseInt(groupId);
    return data.tasks
      .filter(task => {
        const assignedGroups = task.assigned_groups || [];
        const assignedGroupIds = assignedGroups.map(id => parseInt(id));
        
        // 如果任務在 assigned_groups 中，不應該顯示
        if (assignedGroupIds.includes(groupIdInt)) {
          return false;
        }
        
        // 如果任務不在 assigned_groups 中，檢查 customizations 中是否有該小組的狀態記錄
        // 如果有 customizations 記錄，說明任務曾經被指派過（即使現在已刪除），不應該顯示
        const groupStatusKey = `group_${groupIdInt}_status`;
        const hasCustomStatus = task.customizations && task.customizations[groupStatusKey];
        
        // 如果有 customizations 記錄，不應該顯示（已刪除但還有殘留記錄）
        if (hasCustomStatus) {
          return false;
        }
        
        // 如果任務不在 assigned_groups 中，且沒有 customizations 記錄，應該顯示（已刪除的任務）
        return true;
      })
      .map(task => {
        // 提取章節和子任務編號
        let unitNumber = task.unit_number;
        let subtaskNumber = task.subtask_number;
        
        if ((unitNumber === null || unitNumber === undefined) && task.customizations) {
          const customizations = typeof task.customizations === 'string' 
            ? JSON.parse(task.customizations) 
            : task.customizations;
          unitNumber = customizations?.unit_number;
          subtaskNumber = customizations?.subtask_number;
        }
        
        if ((unitNumber === null || unitNumber === undefined) && task.order_index !== null && task.order_index !== undefined) {
          const orderIndex = parseInt(task.order_index) || 0;
          unitNumber = Math.floor(orderIndex / 10000) + 1;
          subtaskNumber = orderIndex % 10000 || 1;
        }
        
        return {
          ...task,
          displayUnitNumber: unitNumber ?? 1,
          displaySubtaskNumber: typeof subtaskNumber === 'number' ? subtaskNumber : (subtaskNumber ? parseInt(subtaskNumber) : 1)
        };
      })
      .sort((a, b) => {
        if (a.displayUnitNumber !== b.displayUnitNumber) {
          return a.displayUnitNumber - b.displayUnitNumber;
        }
        return a.displaySubtaskNumber - b.displaySubtaskNumber;
      });
  };

  // 按章節分組任務
  const groupTasksByUnit = (tasks) => {
    const grouped = {};
    tasks.forEach(task => {
      const unitKey = `unit_${task.displayUnitNumber}`;
      if (!grouped[unitKey]) {
        grouped[unitKey] = {
          unitNumber: task.displayUnitNumber,
          tasks: []
        };
      }
      grouped[unitKey].tasks.push(task);
    });
    return grouped;
  };

  // 獲取可選擇的小組（未指派該任務的小組）- 任務視圖使用
  // 只顯示不在 assigned_groups 中的小組（已刪除的小組）
  // 注意：需要檢查 assigned_groups 和 customizations（不檢查 matrixStatus，因為它會為所有組合創建記錄）
  const getAvailableGroups = (taskId) => {
    const task = data.tasks.find(t => t.id === taskId);
    const assignedGroups = task?.assigned_groups || [];
    // 確保類型一致：將 assignedGroups 和 group.id 都轉為數字進行比較
    const assignedGroupIds = assignedGroups.map(id => parseInt(id));
    return data.groups.filter(group => {
      const groupId = parseInt(group.id);
      
      // 如果小組在 assigned_groups 中，不應該顯示
      if (assignedGroupIds.includes(groupId)) {
        return false;
      }
      
      // 如果小組不在 assigned_groups 中，檢查 customizations 中是否有該小組的狀態記錄
      // 如果有 customizations 記錄，說明小組曾經被指派過（即使現在已刪除），不應該顯示
      const groupStatusKey = `group_${groupId}_status`;
      const hasCustomStatus = task.customizations && task.customizations[groupStatusKey];
      
      // 如果有 customizations 記錄，不應該顯示（已刪除但還有殘留記錄）
      if (hasCustomStatus) {
        return false;
      }
      
      // 如果小組不在 assigned_groups 中，且沒有 customizations 記錄，應該顯示（已刪除的小組）
      return true;
    });
  };

  const getTaskStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />;
      case 'in_progress':
        return <PlayArrowIcon sx={{ color: '#ff9800', fontSize: 18 }} />;
      case 'dispatched':
        return <RadioButtonUncheckedIcon sx={{ color: '#2196f3', fontSize: 18 }} />;
      default:
        return <RadioButtonUncheckedIcon sx={{ color: '#ccc', fontSize: 18 }} />;
    }
  };

  const getTaskTagColor = (tag) => {
    const tagColors = {
      '想法': '#FFF9C4',
      '提問': '#BBDEFB',
      '資訊': '#C8E6C9',
      '實驗': '#FFCDD2',
      '紀錄': '#B2EBF2'
    };
    return tagColors[tag] || '#f5f5f5';
  };

  // 處理進度狀態變更（通過">"按鈕）
  const handleStatusChangeClick = (e, taskId, groupId, currentStatus, isTaskView = false) => {
    e.stopPropagation(); // 阻止事件冒泡，避免觸發卡片點擊
    
    // 如果當前狀態是已完成，不允許變更
    if (currentStatus === 'completed') {
      return;
    }

    // 計算下一個狀態
    let newStatus;
    switch (currentStatus) {
      case 'not_dispatched':
        newStatus = 'dispatched';
        break;
      case 'dispatched':
        newStatus = 'in_progress';
        break;
      case 'in_progress':
        newStatus = 'completed';
        break;
      default:
        newStatus = 'dispatched';
    }

    // 顯示確認對話框，記錄是否為任務視圖
    setStatusChangeData({ taskId, groupId, currentStatus, newStatus, isTaskView });
    setOpenStatusConfirmDialog(true);
  };

  // 確認狀態變更
  const handleConfirmStatusChange = async () => {
    if (!statusChangeData || !onCellClick) {
      setOpenStatusConfirmDialog(false);
      setStatusChangeData(null);
      return;
    }

    const { taskId, groupId, newStatus, isTaskView } = statusChangeData;
    await onCellClick(taskId, groupId, newStatus);
    
    // 如果是任務視圖，確保保持在任務視圖模式
    if (isTaskView && viewMode !== 'task') {
      setViewMode('task');
    }
    
    setOpenStatusConfirmDialog(false);
    setStatusChangeData(null);
  };

  // 移除任務（從指定小組移除）
  const handleRemoveTaskFromGroup = async (taskId, groupId) => {
    if (!window.confirm('確定要從此小組移除任務嗎？只有排程狀態的任務才能移除。')) {
      return;
    }

    try {
      await axiosInstance.put(`${config.removeTaskFromGroup}${taskId}/remove-from-group`, {
        group_id: groupId
      });

      // 刷新數據
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      console.error('移除任務錯誤:', error);
      const errorMessage = error.response?.data?.message || '移除任務失敗';
      alert(errorMessage);
    }
  };

  // 加回任務（將任務重新指派給指定小組）
  const handleRestoreTaskToGroup = async (taskId, groupId) => {
    try {
      await axiosInstance.put(`${config.restoreTaskToGroup}${taskId}/restore-to-group`, {
        group_id: groupId
      });

      // 刷新數據
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      console.error('加回任務錯誤:', error);
      const errorMessage = error.response?.data?.message || '加回任務失敗';
      alert(errorMessage);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const currentState = prev[groupId];
      // 預設展開（undefined 視為 true），點擊後切換
      return {
        ...prev,
        [groupId]: currentState === undefined ? false : !currentState
      };
    });
  };

  const toggleStatus = (groupId, statusId) => {
    const key = `${groupId}_${statusId}`;
    setExpandedStatuses(prev => {
      const currentState = prev[key];
      // 預設展開（undefined 視為 true），點擊後切換
      return {
        ...prev,
        [key]: currentState === undefined ? false : !currentState
      };
    });
  };

  // 任務視圖：按任務分組（按章節/子任務排序）
  const taskViewData = useMemo(() => {
    if (viewMode !== 'task') return null;
    if (!data || !data.tasks || !Array.isArray(data.tasks)) {
      console.warn('⚠️ [TaskBlocksView] 任務視圖數據無效:', data);
      return {};
    }

    console.log('🔄 [TaskBlocksView] 任務視圖數據重新計算');
    console.log('  - 任務總數:', data.tasks.length);
    console.log('  - 小組總數:', data.groups?.length || 0);
    console.log('  - 任務列表:', data.tasks.map(t => ({ id: t.id, title: t.title, assigned_groups: t.assigned_groups })));

    // 先對任務進行排序（按章節/子任務）
    const sortedTasks = [...data.tasks].sort((a, b) => {
      // 優先使用直接屬性，若無則從 customizations 取得章節/子任務
      const aUnit = a.unit_number ?? a.customizations?.unit_number ?? 1;
      const bUnit = b.unit_number ?? b.customizations?.unit_number ?? 1;
      const aSubtask = a.subtask_number ?? a.customizations?.subtask_number ?? null;
      const bSubtask = b.subtask_number ?? b.customizations?.subtask_number ?? null;
      
      // 優先按章節號排序
      if (aUnit !== bUnit) {
        return aUnit - bUnit;
      }
      
      // 同章節內按子任務號排序
      if (aSubtask && bSubtask) {
        // 嘗試解析為數字
        const aSubNum = parseInt(aSubtask);
        const bSubNum = parseInt(bSubtask);
        if (!isNaN(aSubNum) && !isNaN(bSubNum)) {
          return aSubNum - bSubNum;
        }
        // 如果無法解析為數字，按字串排序
        return aSubtask.localeCompare(bSubtask);
      }
      
      // 如果沒有子任務號，按 order_index 排序
      return (a.order_index || 0) - (b.order_index || 0);
    });

    const tasksMap = {};
    
        sortedTasks.forEach(task => {
      const taskGroups = [];
      
      data.groups.forEach(group => {
        const assignedGroups = task.assigned_groups || [];
        const assignedGroupIds = assignedGroups.map(id => parseInt(id));
        const groupIdInt = parseInt(group.id);
        const key = `${task.id}_${group.id}`;
        const cellData = data.matrixStatus?.[key] || {};
        
        // 只有當任務在 assigned_groups 中時，才應該顯示小組
        // 或者，如果任務不在 assigned_groups 中，但 customizations 中有該小組的狀態記錄，也應該顯示（表示曾經被指派過）
        let shouldShow = false;
        if (assignedGroupIds.includes(groupIdInt)) {
          shouldShow = true;
        } else {
          // 檢查 customizations 中是否有該小組的狀態記錄
          const groupStatusKey = `group_${groupIdInt}_status`;
          const hasCustomStatus = task.customizations && task.customizations[groupStatusKey];
          if (hasCustomStatus) {
            shouldShow = true;
          }
        }
        
        if (shouldShow) {
          taskGroups.push({
            group,
            status: cellData.status || 'not_dispatched',
            progress: cellData.progress,
            studentResult: cellData.studentResult
          });
        }
      });

      // 修改：顯示所有任務，即使沒有指派給任何小組
      // 這樣可以確保任務清單的 CRUD 操作能正確反映在任務視圖中
      tasksMap[task.id] = {
        task: {
          ...task,
          // 優先使用直接屬性，若無則從 customizations 提取 unit_number 和 subtask_number
          unit_number: task.unit_number ?? task.customizations?.unit_number ?? null,
          subtask_number: task.subtask_number ?? task.customizations?.subtask_number ?? null
        },
        groups: taskGroups // 如果沒有指派，taskGroups 為空陣列，但仍會顯示任務
      };
    });

    console.log('✅ [TaskBlocksView] 任務視圖數據計算完成');
    console.log('  - 任務視圖中的任務數量:', Object.keys(tasksMap).length);
    console.log('  - 任務視圖中的任務ID列表:', Object.keys(tasksMap));

    return tasksMap;
  }, [data.tasks, data.groups, data.matrixStatus, viewMode]);

  // 數據更新後恢復滾動位置
  useEffect(() => {
    if (viewMode === 'task' && lastOperatedTaskIdRef.current) {
      // 等待 DOM 更新完成後再滾動
      setTimeout(() => {
        if (scrollContainerRef.current) {
          // 嘗試滾動到最後操作的任務位置
          const taskElement = document.getElementById(`task-card-${lastOperatedTaskIdRef.current}`);
          if (taskElement) {
            taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            console.log('✅ [TaskBlocksView] 已滾動到任務:', lastOperatedTaskIdRef.current);
          } else {
            // 如果找不到任務元素，使用保存的滾動位置
            scrollContainerRef.current.scrollTop = lastScrollPositionRef.current;
            console.log('✅ [TaskBlocksView] 已恢復滾動位置:', lastScrollPositionRef.current);
          }
        }
      }, 200);
    }
  }, [data.tasks, viewMode]);

  return (
    <Box>
      {/* 標題和視圖切換 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          任務進度總覽
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
              // 保存視圖模式到 sessionStorage
              sessionStorage.setItem(`taskBlocksViewMode_${courseDispatchId}`, newMode);
            }
          }}
          size="small"
        >
          <ToggleButton value="task">
            <AssignmentIcon sx={{ mr: 1, fontSize: 18 }} />
            任務視圖
          </ToggleButton>
          <ToggleButton value="group">
            <ViewListIcon sx={{ mr: 1, fontSize: 18 }} />
            小組視圖
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 小組視圖 */}
      {viewMode === 'group' && [...data.groups].sort((a, b) => {
        // 按組別ID排序（確保組別順序一致）
        const idA = parseInt(a.id) || 0;
        const idB = parseInt(b.id) || 0;
        if (idA !== idB) return idA - idB;
        // 如果ID相同，按名稱排序
        const nameA = a.name || a.groupName || '';
        const nameB = b.name || b.groupName || '';
        return nameA.localeCompare(nameB, 'zh-TW');
      }).map(group => {
        const sequence = groupTaskSequences[group.id];
        if (!sequence) return null;

        const { tasksByStatus } = sequence;
        const totalTasks = Object.values(tasksByStatus).reduce((sum, tasks) => sum + tasks.length, 0);
        const completedTasks = tasksByStatus.completed.length;
        const isGroupExpanded = expandedGroups[group.id] !== false; // 預設展開

        return (
          <Card 
            key={group.id} 
            sx={{ 
              mb: 3,
              border: '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 2
              }
            }}
          >
            <CardContent>
              {/* 組別標題（可收折） */}
              <Box 
                sx={{ 
                  mb: 2,
                  cursor: 'pointer'
                }}
                onClick={() => toggleGroup(group.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isGroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#2b687f'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroupMembersAnchor(e.currentTarget);
                        setSelectedGroupForMembers(group);
                      }}
                      onMouseEnter={(e) => {
                        setGroupMembersAnchor(e.currentTarget);
                        setSelectedGroupForMembers(group);
                      }}
                    >
                      {group.name}
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${completedTasks} / ${totalTasks} 已完成`} 
                    size="small" 
                    color="primary"
                  />
                </Box>
                <Divider />
              </Box>

              {/* Trello 看板風格：按狀態分欄 */}
              <Collapse in={isGroupExpanded}>
                <Grid container spacing={2}>
                  {statusColumns.map(column => {
                    const tasks = tasksByStatus[column.id] || [];
                    const statusKey = `${group.id}_${column.id}`;
                    const isStatusExpanded = expandedStatuses[statusKey] !== false; // 預設展開

                    return (
                      <Grid item xs={12} sm={6} md={3} key={column.id}>
                        <Box
                          sx={{
                            minHeight: 200,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                            p: 1.5,
                            border: `2px solid ${column.color}`
                          }}
                        >
                          {/* 欄位標題（可收折） */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 1.5
                          }}>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                cursor: 'pointer',
                                flex: 1
                              }}
                              onClick={() => toggleStatus(group.id, column.id)}
                            >
                              {isStatusExpanded ? (
                                <ExpandLessIcon sx={{ fontSize: 18, color: column.textColor }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 18, color: column.textColor }} />
                              )}
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: column.textColor
                                }}
                              >
                                {column.label}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip 
                                label={tasks.length} 
                                size="small" 
                                sx={{
                                  backgroundColor: column.color,
                                  color: column.textColor,
                                  height: 20,
                                  fontSize: '11px'
                                }}
                              />
                            </Box>
                          </Box>

                          {/* 任務卡片列表（可收折） */}
                          <Collapse in={isStatusExpanded}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 1,
                              minHeight: 150
                            }}>
                              {/* 排程狀態顯示新增任務卡片 */}
                              {column.id === 'not_dispatched' && (
                                <Paper
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenTaskSelectDialog(group.id, column.id);
                                  }}
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: '#f8f9fa',
                                    border: '2px dashed #d0d0d0',
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      backgroundColor: '#e8f4f8',
                                      borderColor: '#2b687f',
                                      transform: 'translateY(-2px)',
                                      boxShadow: 2
                                    }
                                  }}
                                >
                                  <AddIcon sx={{ color: '#2b687f', fontSize: 20 }} />
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      color: '#2b687f'
                                    }}
                                  >
                                    新增任務
                                  </Typography>
                                </Paper>
                              )}
                              {tasks.length === 0 && column.id !== 'not_dispatched' ? (
                                <Box sx={{ 
                                  textAlign: 'center', 
                                  py: 4,
                                  color: 'text.secondary',
                                  fontSize: '0.875rem'
                                }}>
                                  無任務
                                </Box>
                              ) : (
                                tasks.map((task, index) => {
                              const isCurrentTask = task.status === 'in_progress';

                              return (
                                <Paper
                                  key={task.id}
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: 'white',
                                    border: isCurrentTask 
                                      ? '2px solid #ff9800' 
                                      : '1px solid #e0e0e0',
                                    borderRadius: 1.5,
                                    position: 'relative',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: 2,
                                      transform: 'translateY(-2px)'
                                    }
                                  }}
                                >
                                  {/* 任務編號和狀態圖標 */}
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    justifyContent: 'space-between',
                                    mb: 0.5,
                                    gap: 1
                                  }}>
                                    {/* 任務編號 */}
                                    <Box sx={{ 
                                      minWidth: 36, 
                                      height: 28, 
                                      backgroundColor: isCurrentTask ? '#ff9800' : '#2b687f',
                                      borderRadius: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      color: 'white',
                                      flexShrink: 0,
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                      px: 0.5
                                    }}>
                                      {(() => {
                                        // 優先使用直接屬性，若無則從 customizations 取得章節/子任務
                                        const unitNumber = task.unit_number ?? task.customizations?.unit_number ?? 1;
                                        const subtaskNumber = task.subtask_number ?? task.customizations?.subtask_number ?? (index + 1);
                                        return `${unitNumber}-${subtaskNumber}`;
                                      })()}
                                    </Box>
                                    
                                    {/* 任務狀態圖標和標籤 */}
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'space-between',
                                      flex: 1,
                                      gap: 0.5
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, flexWrap: 'wrap' }}>
                                        {getTaskStatusIcon(task.status)}
                                        <Chip
                                          label={task.tag || '任務'}
                                          size="small"
                                          sx={{
                                            backgroundColor: getTaskTagColor(task.tag),
                                            color: '#333',
                                            fontSize: '10px',
                                            height: 18,
                                            fontWeight: 500
                                          }}
                                        />
                                      </Box>
                                      {isCurrentTask && (
                                        <Chip 
                                          label="進行中" 
                                          size="small" 
                                          sx={{ 
                                            height: 18,
                                            fontSize: '10px',
                                            backgroundColor: '#ff9800',
                                            color: 'white'
                                          }} 
                                        />
                                      )}
                                    </Box>
                                  </Box>

                                  {/* 任務標題 */}
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      mb: 0.5,
                                      lineHeight: 1.3,
                                      color: '#333',
                                      ml: 3.5 // 為編號留出空間
                                    }}
                                  >
                                    {task.title}
                                  </Typography>

                                  {/* 任務描述（如果有） */}
                                  {task.description && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ 
                                        fontSize: '11px',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        mb: 0.5
                                      }}
                                    >
                                      {task.description}
                                    </Typography>
                                  )}

                                  {/* 進度顯示 */}
                                  {task.progress && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: '10px' }}
                                    >
                                      進度: {task.progress}
                                    </Typography>
                                  )}

                                  {/* 排程狀態的按鈕（派發和刪除） */}
                                  {task.status === 'not_dispatched' && (
                                    <Box sx={{ 
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      display: 'flex',
                                      gap: 0.5
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusChangeClick(e, task.id, group.id, task.status, false);
                                        }}
                                        sx={{
                                          color: '#1976d2',
                                          backgroundColor: '#e3f2fd',
                                          '&:hover': {
                                            backgroundColor: '#bbdefb',
                                            color: '#1565c0'
                                          }
                                        }}
                                        title="派發任務"
                                      >
                                        <ArrowForwardIcon fontSize="small" />
                                      </IconButton>
                                      <Tooltip title="移除任務">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveTaskFromGroup(task.id, group.id);
                                          }}
                                          sx={{
                                            color: '#d32f2f',
                                            '&:hover': {
                                              backgroundColor: '#ffebee',
                                              color: '#c62828'
                                            }
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}
                                  {/* 已派發和進行中狀態的狀態變更按鈕 */}
                                  {task.status === 'dispatched' && (
                                    <Box sx={{ 
                                      position: 'absolute',
                                      top: 4,
                                      right: 4
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusChangeClick(e, task.id, group.id, task.status, false);
                                        }}
                                        sx={{
                                          color: '#2b687f',
                                          '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2'
                                          }
                                        }}
                                        title="變更為進行中"
                                      >
                                        <ArrowForwardIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                  {task.status === 'in_progress' && (
                                    <Box sx={{ 
                                      position: 'absolute',
                                      top: 4,
                                      right: 4
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusChangeClick(e, task.id, group.id, task.status, false);
                                        }}
                                        sx={{
                                          color: '#2b687f',
                                          '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2'
                                          }
                                        }}
                                        title="變更為已完成"
                                      >
                                        <ArrowForwardIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Paper>
                              );
                            })
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}

      {/* 任務視圖 */}
      {viewMode === 'task' && (
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1
          }}
        >
          {Object.values(taskViewData || {}).map(({ task, groups }) => {
        // 按狀態分組
        const groupsByStatus = {
          not_dispatched: [],
          dispatched: [],
          in_progress: [],
          completed: []
        };

        groups.forEach(({ group, status, progress, studentResult }) => {
          groupsByStatus[status].push({ group, status, progress, studentResult });
        });

        const taskKey = `task_${task.id}`;
        const isTaskExpanded = expandedGroups[taskKey] !== false; // 預設展開

        return (
          <Card 
            id={`task-card-${task.id}`}
            key={task.id} 
            sx={{ 
              mb: 3,
              border: '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 2
              }
            }}
          >
            <CardContent>
              {/* 任務標題（可收折） */}
              <Box 
                sx={{ 
                  mb: 2,
                  cursor: 'pointer'
                }}
                onClick={() => toggleGroup(taskKey)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isTaskExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Chip
                      label={task.tag || '任務'}
                      size="small"
                      sx={{
                        backgroundColor: getTaskTagColor(task.tag),
                        color: '#333',
                        fontSize: '11px',
                        height: 20
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {task.title}
                    </Typography>
                  </Box>
                  <Chip 
                    label={groups.length > 0 
                      ? `${groupsByStatus.completed.length} / ${groups.length} 組已完成`
                      : '未指派給任何小組'
                    } 
                    size="small" 
                    color={groups.length > 0 ? "primary" : "default"}
                  />
                </Box>
                <Divider />
              </Box>

              {/* Trello 看板風格：按狀態分欄 */}
              <Collapse in={isTaskExpanded}>
                <Grid container spacing={2}>
                  {statusColumns.map(column => {
                    const groupList = groupsByStatus[column.id] || [];
                    const statusKey = `${taskKey}_${column.id}`;
                    const isStatusExpanded = expandedStatuses[statusKey] !== false; // 預設展開

                    return (
                      <Grid item xs={12} sm={6} md={3} key={column.id}>
                        <Box
                          sx={{
                            minHeight: 200,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                            p: 1.5,
                            border: `2px solid ${column.color}`
                          }}
                        >
                          {/* 欄位標題（可收折） */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 1.5
                          }}>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                cursor: 'pointer',
                                flex: 1
                              }}
                              onClick={() => toggleStatus(taskKey, column.id)}
                            >
                              {isStatusExpanded ? (
                                <ExpandLessIcon sx={{ fontSize: 18, color: column.textColor }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 18, color: column.textColor }} />
                              )}
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: column.textColor
                                }}
                              >
                                {column.label}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip 
                                label={groupList.length} 
                                size="small" 
                                sx={{
                                  backgroundColor: column.color,
                                  color: column.textColor,
                                  height: 20,
                                  fontSize: '11px'
                                }}
                              />
                            </Box>
                          </Box>

                          {/* 小組列表（可收折） */}
                          <Collapse in={isStatusExpanded}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 1,
                              minHeight: 150
                            }}>
                              {/* 排程狀態顯示新增小組卡片 */}
                              {column.id === 'not_dispatched' && (
                                <Paper
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenTaskSelectDialog(task.id, column.id, true); // isTaskView = true
                                  }}
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: '#f8f9fa',
                                    border: '2px dashed #d0d0d0',
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      backgroundColor: '#e8f4f8',
                                      borderColor: '#2b687f',
                                      transform: 'translateY(-2px)',
                                      boxShadow: 2
                                    }
                                  }}
                                >
                                  <AddIcon sx={{ color: '#2b687f', fontSize: 20 }} />
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      color: '#2b687f'
                                    }}
                                  >
                                    新增小組
                                  </Typography>
                                </Paper>
                              )}
                              {groupList.length === 0 && column.id !== 'not_dispatched' ? (
                                <Box sx={{ 
                                  textAlign: 'center', 
                                  py: 4,
                                  color: 'text.secondary',
                                  fontSize: '0.875rem'
                                }}>
                                  無小組
                                </Box>
                              ) : (
                                groupList.map(({ group, progress, studentResult, status }) => (
                                  <Paper
                                    key={group.id}
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: 'white',
                                      border: status === 'in_progress' 
                                        ? '2px solid #ff9800' 
                                        : '1px solid #e0e0e0',
                                      borderRadius: 1.5,
                                      cursor: 'pointer',
                                      position: 'relative',
                                      '&:hover': {
                                        boxShadow: 2,
                                        transform: 'translateY(-2px)'
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    {/* 小組狀態圖標 */}
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'space-between',
                                      mb: 0.5
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getTaskStatusIcon(status)}
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            cursor: 'pointer',
                                            '&:hover': {
                                              textDecoration: 'underline',
                                              color: '#2b687f'
                                            }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setGroupMembersAnchor(e.currentTarget);
                                            setSelectedGroupForMembers(group);
                                          }}
                                          onMouseEnter={(e) => {
                                            setGroupMembersAnchor(e.currentTarget);
                                            setSelectedGroupForMembers(group);
                                          }}
                                        >
                                          {group.name}
                                        </Typography>
                                      </Box>
                                      {status === 'in_progress' && (
                                        <Chip 
                                          label="進行中" 
                                          size="small" 
                                          sx={{ 
                                            height: 18,
                                            fontSize: '10px',
                                            backgroundColor: '#ff9800',
                                            color: 'white'
                                          }} 
                                        />
                                      )}
                                      {/* 進度狀態變更按鈕和刪除按鈕 */}
                                      {status === 'not_dispatched' && (
                                        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => handleStatusChangeClick(e, task.id, group.id, status, true)}
                                            sx={{
                                              color: '#1976d2',
                                              backgroundColor: '#e3f2fd',
                                              '&:hover': {
                                                backgroundColor: '#bbdefb',
                                                color: '#1565c0'
                                              }
                                            }}
                                            title="派發任務"
                                          >
                                            <ArrowForwardIcon fontSize="small" />
                                          </IconButton>
                                          <Tooltip title="移除任務">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveTaskFromGroup(task.id, group.id);
                                              }}
                                              sx={{
                                                color: '#d32f2f',
                                                '&:hover': {
                                                  backgroundColor: '#ffebee',
                                                  color: '#c62828'
                                                }
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                      {status === 'dispatched' && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => handleStatusChangeClick(e, task.id, group.id, status, true)}
                                          sx={{
                                            ml: 'auto',
                                            color: '#2b687f',
                                            '&:hover': {
                                              backgroundColor: '#e3f2fd',
                                              color: '#1976d2'
                                            }
                                          }}
                                          title="變更為進行中"
                                        >
                                          <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      {status === 'in_progress' && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => handleStatusChangeClick(e, task.id, group.id, status, true)}
                                          sx={{
                                            ml: 'auto',
                                            color: '#2b687f',
                                            '&:hover': {
                                              backgroundColor: '#e3f2fd',
                                              color: '#1976d2'
                                            }
                                          }}
                                          title="變更為已完成"
                                        >
                                          <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      {status === 'completed' && (
                                        <Box sx={{ ml: 'auto' }} />
                                      )}
                                    </Box>
                                    {progress && (
                                      <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{ fontSize: '10px' }}
                                      >
                                        進度: {progress}
                                      </Typography>
                                    )}
                                  </Paper>
                                ))
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
        </Box>
      )}

      {/* 任務選擇對話框 */}
      <Dialog 
        open={openTaskSelectDialog} 
        onClose={() => {
          setOpenTaskSelectDialog(false);
          setSelectedTaskForAdd(null);
          setSelectedTasks([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5, fontWeight: 600, fontSize: '1.1rem' }}>
          {selectedTaskForAdd?.isTaskView ? '選擇小組' : '選擇任務'}
          {selectedTaskForAdd && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
              {selectedTaskForAdd.isTaskView ? (
                <>任務：{data.tasks.find(t => t.id === selectedTaskForAdd.groupId)?.title || '未知任務'}</>
              ) : (
                <>指派給：{data.groups.find(g => g.id === selectedTaskForAdd.groupId)?.name || '未知小組'}</>
              )}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          {selectedTaskForAdd && (
            <>
              {selectedTaskForAdd.isTaskView ? (
                // 任務視圖：顯示可選擇的小組
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      共 {getAvailableGroups(selectedTaskForAdd.groupId).length} 個小組
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<SelectAllIcon />}
                      onClick={() => {
                        const allGroups = getAvailableGroups(selectedTaskForAdd.groupId);
                        const allGroupIds = allGroups.map(g => g.id);
                        if (selectedTasks.length === allGroupIds.length) {
                          setSelectedTasks([]);
                        } else {
                          setSelectedTasks(allGroupIds);
                        }
                      }}
                    >
                      {selectedTasks.length === getAvailableGroups(selectedTaskForAdd.groupId).length ? '取消全選' : '全選'}
                    </Button>
                  </Box>
                  <List>
                    {getAvailableGroups(selectedTaskForAdd.groupId).map(group => (
                      <ListItem
                        key={group.id}
                        disablePadding
                        secondaryAction={
                          <Checkbox
                            edge="end"
                            checked={selectedTasks.includes(group.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTasks([...selectedTasks, group.id]);
                              } else {
                                setSelectedTasks(selectedTasks.filter(id => id !== group.id));
                              }
                            }}
                          />
                        }
                      >
                        <ListItemButton>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {group.name}
                              </Typography>
                            }
                            secondary={
                              group.members && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  成員：{Array.isArray(group.members) 
                                    ? group.members.map(member => typeof member === 'object' && member !== null ? (member.name || member.id || '未知成員') : member).join(', ')
                                    : (typeof group.members === 'object' && group.members !== null ? (group.members.name || group.members.id || '未知成員') : group.members)}
                                </Typography>
                              )
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                // 小組視圖：顯示可選擇的任務（按章節分組）
                (() => {
                  const availableTasks = getAvailableTasks(selectedTaskForAdd.groupId);
                  const groupedTasks = groupTasksByUnit(availableTasks);
                  const unitKeys = Object.keys(groupedTasks).sort((a, b) => {
                    return groupedTasks[a].unitNumber - groupedTasks[b].unitNumber;
                  });
                  
                  return (
                    <>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e0e0e0'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          共 {availableTasks.length} 個任務
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<SelectAllIcon sx={{ fontSize: 18 }} />}
                          onClick={() => {
                            const allTaskIds = availableTasks.map(t => t.id);
                            if (selectedTasks.length === allTaskIds.length) {
                              setSelectedTasks([]);
                            } else {
                              setSelectedTasks(allTaskIds);
                            }
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: selectedTasks.length === availableTasks.length ? '#e3f2fd' : 'transparent',
                            color: selectedTasks.length === availableTasks.length ? '#1976d2' : '#666',
                            border: '1px solid',
                            borderColor: selectedTasks.length === availableTasks.length ? '#1976d2' : '#e0e0e0',
                            '&:hover': {
                              backgroundColor: selectedTasks.length === availableTasks.length ? '#bbdefb' : '#f5f5f5',
                              borderColor: '#1976d2'
                            }
                          }}
                        >
                          {selectedTasks.length === availableTasks.length ? '取消全選' : '全選'}
                        </Button>
                      </Box>
                      {unitKeys.length > 0 ? (
                        <List sx={{ p: 0 }}>
                          {unitKeys.map(unitKey => {
                            const unitData = groupedTasks[unitKey];
                            const unitTaskIds = unitData.tasks.map(t => t.id);
                            const allUnitTasksSelected = unitTaskIds.every(id => selectedTasks.includes(id));
                            const someUnitTasksSelected = unitTaskIds.some(id => selectedTasks.includes(id));
                            
                            return (
                              <Accordion
                                key={unitKey}
                                defaultExpanded
                                sx={{
                                  mb: 0.5,
                                  '&:before': { display: 'none' },
                                  boxShadow: 'none',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 1,
                                  '&:not(:last-child)': {
                                    mb: 0.5
                                  }
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                                  sx={{
                                    backgroundColor: '#fafafa',
                                    minHeight: 36,
                                    maxHeight: 36,
                                    py: 0,
                                    px: 1,
                                    '&.Mui-expanded': {
                                      minHeight: 36,
                                      maxHeight: 36
                                    },
                                    '& .MuiAccordionSummary-content': {
                                      my: 0,
                                      alignItems: 'center'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                                      <Checkbox
                                        checked={allUnitTasksSelected}
                                        indeterminate={someUnitTasksSelected && !allUnitTasksSelected}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          if (allUnitTasksSelected) {
                                            setSelectedTasks(selectedTasks.filter(id => !unitTaskIds.includes(id)));
                                          } else {
                                            const newSelected = [...selectedTasks];
                                            unitTaskIds.forEach(id => {
                                              if (!newSelected.includes(id)) {
                                                newSelected.push(id);
                                              }
                                            });
                                            setSelectedTasks(newSelected);
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                      />
                                      <Chip
                                        label={`章節 ${unitData.unitNumber}`}
                                        size="small"
                                        sx={{
                                          backgroundColor: '#e3f2fd',
                                          color: '#1976d2',
                                          fontSize: '0.75rem',
                                          height: 22,
                                          fontWeight: 600,
                                          px: 0.5
                                        }}
                                      />
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 500, 
                                        color: '#666',
                                        fontSize: '0.875rem'
                                      }}>
                                        ({unitData.tasks.length} 個任務)
                                      </Typography>
                                    </Box>
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (allUnitTasksSelected) {
                                          setSelectedTasks(selectedTasks.filter(id => !unitTaskIds.includes(id)));
                                        } else {
                                          const newSelected = [...selectedTasks];
                                          unitTaskIds.forEach(id => {
                                            if (!newSelected.includes(id)) {
                                              newSelected.push(id);
                                            }
                                          });
                                          setSelectedTasks(newSelected);
                                        }
                                      }}
                                      sx={{
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        px: 1,
                                        py: 0.25,
                                        minWidth: 'auto',
                                        height: 24,
                                        borderRadius: 0.5,
                                        backgroundColor: allUnitTasksSelected ? '#e3f2fd' : 'transparent',
                                        color: allUnitTasksSelected ? '#1976d2' : '#666',
                                        border: '1px solid',
                                        borderColor: allUnitTasksSelected ? '#1976d2' : '#d0d0d0',
                                        '&:hover': {
                                          backgroundColor: allUnitTasksSelected ? '#bbdefb' : '#f5f5f5',
                                          borderColor: '#1976d2'
                                        }
                                      }}
                                    >
                                      {allUnitTasksSelected ? '已選' : '全選'}
                                    </Button>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0, bgcolor: '#fff' }}>
                                  <List sx={{ p: 0 }}>
                                    {unitData.tasks.map((task, index) => (
                                      <ListItem
                                        key={task.id}
                                        disablePadding
                                        sx={{
                                          borderBottom: index < unitData.tasks.length - 1 ? '1px solid #f0f0f0' : 'none',
                                          py: 0.5,
                                          px: 1,
                                          '&:hover': {
                                            backgroundColor: '#fafafa'
                                          }
                                        }}
                                        secondaryAction={
                                          <Checkbox
                                            edge="end"
                                            checked={selectedTasks.includes(task.id)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedTasks([...selectedTasks, task.id]);
                                              } else {
                                                setSelectedTasks(selectedTasks.filter(id => id !== task.id));
                                              }
                                            }}
                                            size="small"
                                          />
                                        }
                                      >
                                        <ListItemButton sx={{ py: 0.5, px: 1 }}>
                                          <ListItemText
                                            primary={
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                <Chip
                                                  label={`${task.displayUnitNumber}-${task.displaySubtaskNumber}`}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontSize: '0.7rem',
                                                    height: 18,
                                                    fontWeight: 500,
                                                    minWidth: 36
                                                  }}
                                                />
                                                <Chip
                                                  label={task.tag || '任務'}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: getTaskTagColor(task.tag),
                                                    color: '#333',
                                                    fontSize: '0.7rem',
                                                    height: 18
                                                  }}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                                  {task.title}
                                                </Typography>
                                              </Box>
                                            }
                                            secondary={
                                              task.description && (
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.75rem' }}>
                                                  {task.description}
                                                </Typography>
                                              )
                                            }
                                            sx={{ my: 0 }}
                                          />
                                        </ListItemButton>
                                      </ListItem>
                                    ))}
                                  </List>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </List>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            沒有可選擇的任務
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => {
              setOpenTaskSelectDialog(false);
              setSelectedTaskForAdd(null);
              setSelectedTasks([]);
            }}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            取消
          </Button>
          <Button 
            variant="contained"
            onClick={handleConfirmTaskSelect}
            disabled={isAssigning || selectedTasks.length === 0}
            sx={{
              backgroundColor: '#2b687f',
              '&:hover': { backgroundColor: '#1e4d5a' }
            }}
          >
            確定 ({selectedTasks.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* 狀態變更確認對話框 */}
      <Dialog 
        open={openStatusConfirmDialog} 
        onClose={() => {
          setOpenStatusConfirmDialog(false);
          setStatusChangeData(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          確認變更任務狀態
        </DialogTitle>
        <DialogContent>
          {statusChangeData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                確定要將任務狀態從「{getStatusText(statusChangeData.currentStatus)}」變更為「{getStatusText(statusChangeData.newStatus)}」嗎？
              </Typography>
              {statusChangeData.newStatus === 'completed' && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                 </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => {
            setOpenStatusConfirmDialog(false);
            setStatusChangeData(null);
          }}>
            取消
          </Button>
          <Button variant="contained" onClick={handleConfirmStatusChange}>
            確認
          </Button>
        </DialogActions>
      </Dialog>

      {/* 組員成員列表 Popover */}
      <Popover
        open={Boolean(groupMembersAnchor)}
        anchorEl={groupMembersAnchor}
        onClose={() => {
          setGroupMembersAnchor(null);
          setSelectedGroupForMembers(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onMouseLeave={() => {
          setGroupMembersAnchor(null);
          setSelectedGroupForMembers(null);
        }}
        disableRestoreFocus
      >
        {selectedGroupForMembers && (
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedGroupForMembers.name} - 組員
            </Typography>
            <List dense>
              {selectedGroupForMembers.members && selectedGroupForMembers.members.length > 0 ? (
                selectedGroupForMembers.members.map((member, index) => (
                  <ListItem key={member.id || index} disablePadding>
                                    <ListItemText
                      primary={member.name || '未知成員'}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontSize: '0.875rem' }
                      }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary="暫無組員"
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { fontSize: '0.875rem', color: 'text.secondary' }
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </Popover>
    </Box>
  );
}
